/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import { useEffect, useState } from 'react';
import { useLanguage } from '@/providers/LanguageProvider';
import { toast } from 'sonner';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CustomButton } from '@/components/common';
import { Icons } from '@/components/icons';

export type FormatType = {
  id: string;
  label: string;
  mimeType?: string;
  quality?: number;
};

interface ChartDownloadProps {
  chartId: string;
  filename?: string;
  formats?: FormatType[];
  bgColor?: string;
}

const ChartDownload: React.FC<ChartDownloadProps> = ({
  chartId,
  filename = 'chart',
  formats,
  bgColor = '#082530',
}) => {
  const { messages } = useLanguage();
  const isSankey = chartId.includes('sankey');
  const [isChartPresent, setIsChartPresent] = useState(false);

  const defaultFormats: FormatType[] = [
    {
      id: 'png',
      label: `${messages?.General?.download_png}`,
      mimeType: 'image/png',
    },
    {
      id: 'jpg',
      label: `${messages?.General?.download_jpg}`,
      mimeType: 'image/jpeg',
      quality: 0.9,
    },
    { id: 'svg', label: `${messages?.General?.download_svg}` },
    { id: 'copy', label: `${messages?.SettingPage?.copy_to_clipboard}` },
  ];

  const exportFormats = formats || defaultFormats;

  useEffect(() => {
    const checkChart = () => {
      const chart = document.getElementById(chartId);
      if (chart) {
        setIsChartPresent(true);
      } else {
        setTimeout(checkChart, 500);
      }
    };
    checkChart();
  }, [chartId]);

  // download handler for all formats
  const downloadChart = (format: FormatType) => {
    const container = document.getElementById(chartId);
    const svgElement =
      container?.querySelector('svg') || document.getElementById(chartId);

    if (!svgElement) return;

    const svgRect = svgElement.getBoundingClientRect();
    const width = svgRect.width;
    const height = svgRect.height;

    const clonedSvg = svgElement.cloneNode(true) as SVGElement;
    clonedSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    clonedSvg.setAttribute('width', width.toString());
    clonedSvg.setAttribute('height', height.toString());

    const svgString = new XMLSerializer().serializeToString(clonedSvg);
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml' });
    const svgUrl = URL.createObjectURL(svgBlob);

    // handler for SVG vs raster formats
    if (format.id === 'svg') {
      // SVG download
      const link = document.createElement('a');
      link.href = svgUrl;
      link.download = `${filename}.svg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(svgUrl);
    } else if (format.id === 'copy') {
      // copy to clipboard
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, width, height);

      const img = new Image();
      img.onload = async () => {
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(svgUrl);

        // Convert canvas to PNG Blob
        canvas.toBlob(async (blob) => {
          if (!blob) return;
          try {
            const clipboardItem = new ClipboardItem({ 'image/png': blob });
            await navigator.clipboard.write([clipboardItem]);
            toast.success(
              messages?.SettingPage?.visualization_copied ||
                'Visualization copied successfully'
            );
          } catch (err) {
            toast.error(
              messages?.SettingPage?.visualization_copy_failed ||
                'Failed to copy visualization'
            );
          }
        });
      };
      img.src = svgUrl;
    } else {
      // Canvas-based formats (PNG, JPG, etc)
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // apply background color
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, width, height);

      // draw SVG on canvas
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(svgUrl);

        // convert to requested format
        const dataUrl = canvas.toDataURL(
          format.mimeType || 'image/png',
          format.quality
        );

        // download the image
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = `${filename}.${format.id}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };

      img.src = svgUrl;
    }
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <CustomButton
          variant="ghost"
          className="size-6 p-0 text-gray-400 hover:bg-[#257C9D0F] hover:text-white"
          disabled={!isChartPresent}
        >
          {Icons.DownloadIcon(
            isSankey ? 'h-4 w-4 text-[#C1C9CF] shrink-0' : 'h-4 w-4 text-ocean'
          )}
        </CustomButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="cursor-pointer rounded-none bg-white p-0 text-h5 text-black"
      >
        {exportFormats.map((format) => (
          <DropdownMenuItem
            key={format.id}
            onClick={() => downloadChart(format)}
            className="flex cursor-pointer items-center gap-2 p-2 hover:bg-gray-100"
          >
            {format.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export { ChartDownload };
